import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Dropdown, MenuProps, Tooltip } from 'antd';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import getPopupContainer from 'utils/getPopupContainer';
import { sortStageAndStatusDropdownOptions } from 'utils/sortStageAndStatusDropdownOptions';

import { FormattedOption } from 'types/dictionaries';
import { TranslationKey } from 'types/i18next';
import { StageSelectorDropdownConfig } from 'types/stageSelectorDropdown';

import {
  ApplicationStage,
  ClientApplicationStages,
  Dictionaries,
  JobStatus,
  JobseekerStage
} from 'constants/enums';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowDownSmall.svg';

interface StageSelectorProps {
  value?:
    | JobseekerStage
    | ApplicationStage
    | ClientApplicationStages
    | JobStatus
    | null;
  id: string;
  options: FormattedOption[];
  isDisplayIcons?: boolean;
  hasPermission?: boolean;
  config: StageSelectorDropdownConfig;
  handleChange?: (id: string) => MenuProps['onClick'];
  dictionary?: Dictionaries;
  staticMode?: boolean;
}

const StageSelector: FC<StageSelectorProps> = ({
  value,
  id,
  options,
  config,
  isDisplayIcons = true,
  hasPermission = true,
  handleChange,
  staticMode = false,
  dictionary = ''
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const stageTransitions = useSelector(selectDictionary(dictionary));

  const {
    stageClassNames,
    stageIcons,
    handleChangeStage,
    configStyles,
    disabledStageTooltips
  } = config;

  const availableStages = value
    ? stageTransitions.filter((transition) => transition.parentKey === value)
    : [];

  const items = sortStageAndStatusDropdownOptions(
    availableStages,
    Dictionaries.JobseekerStage
  ).map((stage) => ({
    key: stage.value,
    className: stageClassNames[stage.value],
    label: stage.label,
    icon: stageIcons[stage.value]
  }));

  const handleDropdownChange = handleChange
    ? handleChange(id)
    : handleChangeStage?.(id, dispatch, t);

  const renderItem = (value?: string) => {
    const option = options.find((opt) => opt.value === value);
    const className = option ? stageClassNames[option.value] : '';
    const IconComponent = option ? stageIcons[option.value] : null;

    if (!option) {
      return t('common.label.selectValue');
    }

    const isIcon = isDisplayIcons && IconComponent;

    return (
      <div className={`${className} ${configStyles.option}`}>
        {isIcon && <IconComponent className={configStyles.statusIcon} />}
        {option.label}
      </div>
    );
  };

  const dropdownItems = items.map((item) => ({
    key: item.key,
    label: renderItem(item.key)
  }));

  if (staticMode || !hasPermission) {
    return (
      <div
        className={`${configStyles.dropdownLink} ${
          value ? stageClassNames[value] : ''
        }`}
      >
        {renderItem(value?.toString())}
      </div>
    );
  }

  const isClickable = dropdownItems.length;
  const tooltipKey = disabledStageTooltips?.find(
    (el) => el.stage === value
  )?.text;
  const tooltip = tooltipKey ? t(tooltipKey as TranslationKey) : undefined;

  return isClickable ? (
    <Tooltip title={tooltip} trigger="hover" placement="right">
      <Dropdown
        menu={{ items: dropdownItems, onClick: handleDropdownChange }}
        rootClassName={configStyles.dropdownContainer}
        getPopupContainer={getPopupContainer}
      >
        <a
          className={`${configStyles.dropdownLink} ${
            value ? stageClassNames[value] : ''
          }`}
          onClick={(e) => e.preventDefault()}
        >
          {renderItem(value?.toString())}
          <ArrowDownIcon />
        </a>
      </Dropdown>
    </Tooltip>
  ) : (
    <Tooltip title={tooltip} trigger="hover" placement="right">
      <div
        className={`${configStyles.dropdownLink} ${value ? stageClassNames[value] : ''}`}
      >
        {renderItem(value?.toString())}
      </div>
    </Tooltip>
  );
};

export default StageSelector;
